<template>
  <div>
    <form @submit="onSubmitForm" @reset="onReset">
      <!--
        Favourites row — label (2/12) on the right, preset buttons
        (10/12) on the left. tw-items-baseline keeps the label
        aligned with the button text baselines.
      -->
      <div class="tw-grid tw-grid-cols-12 tw-items-baseline pb-4">
        <div class="tw-col-span-2 tw-text-end">
          <u>Favourites:</u>
        </div>
        <div class="tw-col-span-10 ps-4">
          <!-- Flex wrapper with `gap-2` gives consistent breathing room
               between the favourite preset buttons (Domestic, Inter-
               national, Travel between, Travel between (return)) and
               lets them wrap onto a second row on narrow viewports.
               Variant flips primary (filled) when the preset is active,
               secondary (outlined gray) otherwise — gives clear visual
               feedback for the selected preset without the
               `outline-primary` variant that UiButton doesn't ship. -->
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <UiButton
              v-for="prefill in Object.keys(prefills)"
              :key="prefill"
              :variant="prefill === activePrefill ? 'primary' : 'secondary'"
              @click="onPrefill(prefill)"
            >
              {{ prefill }}
            </UiButton>
          </div>
        </div>
      </div>

      <!-- Advanced section header — label only, sits in the same
           2/12 right-aligned position as the Favourites label. -->
      <div class="tw-grid tw-grid-cols-12">
        <div class="tw-col-span-2 tw-text-end">
          <u>Advanced:</u>
        </div>
      </div>

      <div v-for="(filter, index) in filters" :key="filter.id">
        <FilterRow
          :ref="`filter-${index}`"
          :filter="filter"
          :show-condition="index !== 0"
          :on-remove="() => removeRow(index)"
          :after-update="(field, value) => afterUpdate(index, field, value)"
          :tab-index="filter.id"
          @update:filter="val => filters[index] = val"
        />
        <div v-if="errors && (v$ as any).filters?.$each?.[index]?.$invalid" class="text-center text-danger">
          Field required
        </div>
      </div>

      <!-- Add-row button — right-aligned via flex justify-end. -->
      <div class="tw-flex tw-justify-end pt-2 pb-4">
        <UiButton
          @click.stop="addRow"
          variant="outline-success"
        >
          <i class="fa fa-plus" />
        </UiButton>
      </div>

      <div class="d-flex justify-content-end gap-2 mt-3 pt-3 pb-3 border-top">
        <UiButton
          v-if="filters.filter(row => row.criteria !== null).length > 0"
          variant="danger"
          @click="onReset"
        >
          Reset
        </UiButton>
        <!-- Explicit @click — `type="submit"` was relying on native form
             submit bubbling, which doesn't reach the b-form's @submit
             listener once CountryFilter sits inside UiModal's
             teleport-to-body. Symptom: clicking OK did nothing (filter
             never saved, reopening the modal reverted to defaults). -->
        <UiButton @click="onSubmitForm">
          OK
        </UiButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PropType } from 'vue'
import useVuelidate from '@vuelidate/core'
import { requiredIf, minLength } from '@vuelidate/validators'

import FilterRow from './FilterRow.vue'

import {
  getDefaultRow, getPrefills, isPrefill,
  TRAVEL_BETWEEN, TRAVEL_BETWEEN_RETURN,
} from '.'

const emit = defineEmits(['submit'])

const props = defineProps({
  initialValue: {
    required: false,
    type: Array as PropType<any[]>,
    default: () => []
  },
  // NOTE: this used to be `onSubmit`, but in Vue 3 a prop named
  // `onSubmit` collides with the parent's `@submit="..."` listener
  // (Vue compiles event listeners to `on<EventName>` keys, and the
  // listener overwrites the bound prop). Result: `props.onSubmit`
  // ended up being whatever was bound to `@submit`, which silently
  // hid the modal without ever saving the filter. Renaming to
  // `submitHandler` decouples the two and lets the prop be bound
  // independently of any event listener.
  submitHandler: {
    required: true,
    type: Function
  },
})

const filters = ref([...props.initialValue] as any[])
const lastId = ref(props.initialValue.length)
const errors = ref('')
const activePrefill = ref(null)
const prefills = ref(null)

const v$ = useVuelidate({
  filters: {
    $each: {
      countries: {
        required: requiredIf(((row: any) => row.criteria !== null) as any),
        minLength: minLength(1)
      },
    },
  }
}, { filters })

function loadPrefills () {
   return getPrefills()
}

function addRow ( values: any = {} ) {
  filters.value.push({
    id: lastId.value,
    ...getDefaultRow(),
    ...values
  })
  lastId.value += 1
  activePrefill.value = null
}

function removeRow (index) {
  filters.value.splice(index, 1)
  activePrefill.value = null
}

function onSubmitForm (evt?: Event) {
  // Optional chain — invoked from either `@click` on the OK button (no
  // form submit event) or from b-form's `@submit` (legacy Enter-key
  // handler) which does pass an event. preventDefault is harmless
  // either way.
  evt?.preventDefault?.()

  if (v$.value.$invalid) {
    errors.value = 'Please fill in the required fields'
  } else {
    // clean up filters
    filters.value = filters.value.filter(row => row.criteria !== null)
    // trigger submit (saves the filter via the parent's handler)
    props.submitHandler(filters.value)
    // hide the modal
    emit('submit')
    errors.value = ''
  }
}

function onReset () {
  // reset the form — invoked from explicit `@click` on the Reset
  // button now (was `type="reset"` triggering native form reset, but
  // that proved unreliable with UiModal's Teleport-to-body).
  filters.value = []
  addRow()
  errors.value = ''
}

function onPrefill (prefill: string) {
  // Prefill is passed explicitly from the click handler now (was
  // `evt.target.value` reading the button's `value` attribute, which
  // depended on $attrs fallthrough and target equality with the
  // button element — both fragile).
  // do nothing it is already selected
  if (prefill === activePrefill.value) return

  // if swapping between 'travel between' filters, do not delete countries
  if (
    [TRAVEL_BETWEEN, TRAVEL_BETWEEN_RETURN].indexOf(prefill) !== -1 &&
    [TRAVEL_BETWEEN, TRAVEL_BETWEEN_RETURN].indexOf(activePrefill.value) !== -1
  ) {
    filters.value = filters.value.slice(0, 2)
    prefills.value[prefill].forEach((row, index) => { if (index >= 2) {
      addRow({...row, countries: filters.value[index - 2].countries})
    }})
  } else {
    // otherwise empty filters and autofill all rows
    filters.value = []
    prefills.value[prefill].forEach(row => addRow(row))
  }
  // update active prefill
  activePrefill.value = prefill
}

function afterUpdate (filterIndex, field, value) {
  if (['criteria', 'condition'].indexOf(field) !== -1) {
    // reset active prefill whenever criteria or conditions have changed
    activePrefill.value = null
  }

  // special events after specific filter updates
  if (activePrefill.value === TRAVEL_BETWEEN_RETURN) {
    if (field === 'countries') {
      // when countries changed, sync it with the other field
      if (filterIndex < 2) filters.value[filterIndex + 2].countries = value
    }
  }
}

// created lifecycle equivalent
prefills.value = loadPrefills()

// add an initial empty row
if (filters.value.length === 0) {
  addRow()
} else {
  const prefill = isPrefill(filters.value)
  if (prefill) activePrefill.value = prefill
}

watch(() => props.initialValue, ( newFilters ) => {
  filters.value = newFilters
})

defineExpose({ filters, onPrefill })
</script>
